<template>
    <DxScrollView class="opportunity-details-container">
        <div v-if="contact">
            <div class="back-button-section">
                <DxButton icon="arrowleft" text="Înapoi la listă" styling-mode="text" @click="$emit('back')"
                    class="back-btn" />
            </div>

            <div class="opportunity-header">
                <div class="header-top">
                    <div class="header-left">
                        <div class="opportunity-icon">
                            <i class="dx-icon-box"></i>
                        </div>
                        <div class="header-info">
                            <h1 class="opportunity-title">
                                {{ contact.Nume }} {{ contact.Prenume || '' }}
                            </h1>
                            <div class="opportunity-meta">
                                <span class="meta-item meta-item--function" title="Funcție">
                                    <i class="dx-icon-info"></i>
                                    {{ contact.Titlu || 'Fără funcție' }}
                                </span>
                                <span class="meta-item meta-item--email" title="Email" v-if="contact.Email">
                                    <i class="dx-icon-email "></i>
                                    {{ contact.Email }}
                                </span>
                                <span class="meta-item meta-item--phone" title="Telefon Mobil" v-if="contact.TelMobil">
                                    <i class="dx-icon-tel "></i>
                                    {{ contact.TelMobil }}
                                </span>
                                <span
                                    class="meta-item meta-item--account"
                                    :class="{ 'meta-item--clickable': contact.AccountID }"
                                    :title="contact.AccountID ? 'Click pentru detalii partener' : 'Cont / Partener'"
                                    @click="openPartener">
                                    <i class="dx-icon-home"></i>
                                    {{ contact.Account?.Nume || contact.AccountNume || 'Fără partener' }}
                                    <i v-if="contact.AccountID" class="dx-icon-search meta-item--link-icon"></i>
                                </span>
                                <span class="meta-item meta-item--owner" title="Responsabil (Owner)">
                                    <i class="dx-icon-user"></i>
                                    {{ contact.Owner || 'Fără agent' }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="header-actions">
                    </div>
                </div>
            </div>

            <div class="two-column-layout">
                <div class="left-column">
                    <div class="details-card">
                        <div class="card-header">
                            <div class="card-header-icon">
                                <i class="dx-icon-doc"></i>
                            </div>
                            <h3 class="card-header-title">Detalii Contact</h3>
                        </div>
                        <div class="card-body">
                            <contactForm :opportunity-data="contact" :is-editing="false"
                                @saved="$emit('refresh-data')" />
                        </div>
                    </div>
                </div>

                <div class="right-column">
                    <div class="section-card">
                        <div class="card-header">
                            <div class="card-header-icon"
                                style="background: linear-gradient(135deg, #10b981 0%, #34d399 100%);">
                                <i class="dx-icon-event"></i>
                            </div>
                            <h3 class="card-header-title">Oportunități</h3>
                        </div>
                        <div class="card-body">
                            <CardOpportunities :contact-id="contact?.ID" :opportunity-data="contact"
                                :account-id="contact?.AccountID" />
                        </div>
                    </div>
                </div>
            </div>
        <!-- Partener Details Popup -->
        <DxPopup v-model:visible="isPartenerPopupVisible" :drag-enabled="false" :close-on-outside-click="true"
            :show-title="false" :full-screen="true" :show-close-button="false">
            <div style="height: 100%; overflow: hidden; position: relative;">
                <DxButton icon="close" styling-mode="text" class="popup-close-btn" @click="isPartenerPopupVisible = false" />
                <div v-if="isLoadingPartener" class="popup-loading">
                    <i class="dx-icon-refresh popup-loading-icon"></i>
                    <span>Se încarcă...</span>
                </div>
                <GridParteneriDetalii v-else-if="selectedPartenerData" :partener="selectedPartenerData" @back="isPartenerPopupVisible = false" />
            </div>
        </DxPopup>
        </div>

        <div v-else class="no-opportunity">
            <div class="no-opportunity-icon">
                <i class="dx-icon-box"></i>
            </div>
            <p class="no-opportunity-text">Niciun contact selectat</p>
            <p class="no-opportunity-subtext">Selectează un contact din listă pentru a vedea detaliile</p>
        </div>
    </DxScrollView>
</template>

<script lang="ts">

import { defineComponent } from 'vue';
import { DxScrollView } from 'devextreme-vue/scroll-view';
import { DxButton } from 'devextreme-vue/button';
import OpportunityForm from '@/components/library/opportunity-form.vue';
import CardActivity from '@/components/library/card-activity.vue';
import CardProducts from '@/components/library/card-products.vue';
import contactForm from '../../../components/library/contact-form.vue';
import CardOpportunities from '@/components/library/card-opportunities.vue';
import GridParteneriDetalii from './GridParteneriDetalii.vue';
import { DxPopup } from 'devextreme-vue/popup';
import CrmParteneri from '@/services/Comercial/CrmParteneri';

export default defineComponent({
    components: {
        DxScrollView,
        DxButton,
        OpportunityForm,
        CardActivity,
        CardProducts,
        contactForm,
        CardOpportunities,
        GridParteneriDetalii,
        DxPopup
    },
    props: {
        contact: Object,
    },
    data() {
        return {
            isPartenerPopupVisible: false,
            selectedPartenerData: null as any,
            isLoadingPartener: false,
            statusSteps: [
                'Nou',
                'Identificat',
                'Calificare',
                'Alte analize',
                'Demo',
                'Ofertare',
                'Negociere',
                'Câștigat'
            ]
        }
    },
    computed: {
        currentStatusIndex(): number {
            if (!this.opportunity || !this.opportunity.Status) return -1;
            // Handle "Închis" as a special case or map it to the end if appropriate
            if (this.opportunity.Status === 'Închis') return this.statusSteps.length;
            return this.statusSteps.indexOf(this.opportunity.Status);
        },
        isCurrentStatusComplete(): boolean {
            return this.opportunity?.Status === 'Câștigat' || this.opportunity?.Status === 'Închis';
        }
    },
    methods: {
        getStepClass(step: string): string {
            if (!this.opportunity || !this.opportunity.Status) return 'future';

            const stepIndex = this.statusSteps.indexOf(step);
            const currentIndex = this.currentStatusIndex;

            if (this.opportunity.Status === 'Închis' && step === 'Câștigat') return 'future'; // Or 'lost' style if we had one

            if (this.opportunity.Status === step) return 'current';
            if (stepIndex < currentIndex) return 'completed';
            return 'future';
        },
        handleStepClick(step: string) {
            // Here you could implement logic to change status on click
            // For now, we just log it or maybe emit an event
            console.log('Clicked step:', step);
        },
        formatCurrency(value: number): string {
            if (!value && value !== 0) return '-';
            return new Intl.NumberFormat('ro-RO', {
                style: 'currency',
                currency: 'RON',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        },
        formatDate(dateString: string): string {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('ro-RO', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).format(date);
        },
        getProbabilityColor(probability: number): string {
            if (probability >= 75) return '#10b981';
            if (probability >= 50) return '#3b82f6';
            if (probability >= 25) return '#f59e0b';
            return '#ef4444';
        },
        async openPartener() {
            const partenerId = (this.contact as any)?.AccountID;
            if (!partenerId) return;

            this.isLoadingPartener = true;
            this.selectedPartenerData = null;
            this.isPartenerPopupVisible = true;

            try {
                const response = await CrmParteneri.getById(partenerId);
                this.selectedPartenerData = response.data ?? null;
                if (!this.selectedPartenerData) {
                    this.isPartenerPopupVisible = false;
                }
            } catch (err) {
                console.error(err);
                this.isPartenerPopupVisible = false;
            } finally {
                this.isLoadingPartener = false;
            }
        }
    },
});
</script>

<style scoped lang="scss">
.opportunity-details-container {
    background: #f8f9fa;
    min-height: 100vh;
}

/* Back Button */
.back-button-section {
    padding: 1rem 1.5rem 0.5rem;

    .back-btn {
        color: #6b7280;
        font-weight: 500;

        &:hover {
            color: #ff2c21;
        }
    }
}

/* Header Section */
.opportunity-header {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e5e7eb;
    padding: 1.5rem 1.5rem 1rem;
    /* Reduced bottom padding */
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
}

.header-left {
    display: flex;
    gap: 1rem;
    flex: 1;
    min-width: 300px;
}

.opportunity-icon {
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #ff2c21 0%, #ff5447 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(255, 44, 33, 0.2);

    i {
        font-size: 28px;
        color: white;
    }
}

.header-info {
    flex: 1;
}

.opportunity-title {
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 0.5rem 0;
    line-height: 1.3;
}

.opportunity-meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    color: #6b7280;
    font-size: 14px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 4px 12px;
    border-radius: 20px;
    background: white;
    border: 1px solid #e5e7eb;
    font-weight: 500;
    transition: all 0.2s ease;

    i {
        font-size: 14px;
    }

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    &--function {
        background: #fefce8;
        color: #a16207;
        border-color: #fef9c3;

        i {
            color: #eab308;
        }
    }

    &--email {
        background: #eff6ff;
        color: #1d4ed8;
        border-color: #dbeafe;

        i {
            color: #3b82f6;
        }
    }

    &--phone {
        background: #ecfdf5;
        color: #065f46;
        border-color: #d1fae5;

        i {
            color: #10b981;
        }
    }

    &--account {
        background: #f0fdf4;
        color: #15803d;
        border-color: #dcfce7;

        i {
            color: #22c55e;
        }
    }

    &--owner {
        background: #faf5ff;
        color: #7e22ce;
        border-color: #f3e8ff;

        i {
            color: #a855f7;
        }
    }
}

.meta-separator {
    display: none;
}

.meta-item--clickable {
    cursor: pointer;

    &:hover {
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12) !important;
        transform: translateY(-2px) !important;
    }

    .meta-item--link-icon {
        font-size: 11px;
        opacity: 0;
        transition: opacity 0.15s ease;
        margin-left: 2px;
    }

    &:hover .meta-item--link-icon {
        opacity: 0.7;
    }
}

.popup-close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    width: 36px !important;
    height: 36px !important;
    background: rgba(0, 0, 0, 0.06) !important;
    border-radius: 50% !important;
    color: #374151 !important;

    &:hover {
        background: rgba(0, 0, 0, 0.12) !important;
        color: #111827 !important;
    }

    :deep(.dx-icon) {
        font-size: 16px !important;
    }
}

.popup-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    gap: 12px;
    color: #6b7280;
    font-size: 14px;
}

.popup-loading-icon {
    font-size: 32px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Status Path Styling */
.status-path-container {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
}

.status-path {
    display: flex;
    flex: 1;
    background: #e5e7eb;
    border-radius: 20px;
    /* Rounded pill shape for the whole bar */
    overflow: hidden;
    height: 36px;
}

.status-step {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1.5rem 0 2rem;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:first-child {
        padding-left: 1.5rem;
    }

    /* Chevron Arrow Effect */
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: -12px;
        /* Adjust based on angle */
        width: 0;
        height: 0;
        border-top: 18px solid transparent;
        border-bottom: 18px solid transparent;
        border-left: 12px solid #f3f4f6;
        /* Match background */
        z-index: 2;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        right: -13px;
        /* Border width offset */
        width: 0;
        height: 0;
        border-top: 18px solid transparent;
        border-bottom: 18px solid transparent;
        border-left: 12px solid white;
        /* Separator color */
        z-index: 1;
    }

    /* Completed State */
    &.completed {
        background: #10b981;
        /* Green */
        color: white;

        &::after {
            border-left-color: #10b981;
        }

        &:hover {
            background: #059669;

            &::after {
                border-left-color: #059669;
            }
        }
    }

    /* Current State */
    &.current {
        background: #3b82f6;
        /* Blue */
        color: white;

        &::after {
            border-left-color: #3b82f6;
        }
    }

    /* Future State */
    &.future {
        background: #f3f4f6;
        color: #6b7280;

        &:hover {
            background: #e5e7eb;

            &::after {
                border-left-color: #e5e7eb;
            }
        }
    }

    /* Last child adjustments */
    &:last-child {
        padding-right: 1.5rem;

        &::after,
        &::before {
            display: none;
        }
    }
}

.current-status-action {
    flex-shrink: 0;
}

/* Metrics Section */
.metrics-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
    padding: 0 1.5rem 1.5rem;
}

.metric-card {
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    transition: all 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        transform: translateY(-2px);
    }
}

.metric-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
        font-size: 24px;
    }
}

.metric-content {
    flex: 1;
    min-width: 0;
}

.metric-label {
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
    margin-bottom: 0.375rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.metric-value {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
}

.metric-value-with-bar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.probability-bar-small {
    width: 100%;
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
}

.probability-fill-small {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s ease;
}

/* Two Column Layout */
.two-column-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    padding: 0 1.5rem 1.5rem;
    align-items: start;
}

.left-column {
    display: flex;
    flex-direction: column;
}

.right-column {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.details-card,
.section-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.card-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #f3f4f6;
    background: linear-gradient(to bottom, #ffffff 0%, #fafafa 100%);
}

.card-header-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
        font-size: 18px;
        color: white;
    }
}

.card-header-title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    line-height: 1.3;
}

.card-body {
    padding: 1.5rem;
}

/* No Opportunity State */
.no-opportunity {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
}

.no-opportunity-icon {
    width: 80px;
    height: 80px;
    background: #f3f4f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;

    i {
        font-size: 40px;
        color: #9ca3af;
    }
}

.no-opportunity-text {
    font-size: 18px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 0.5rem 0;
}

.no-opportunity-subtext {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
    .opportunity-header {
        padding: 1rem;
    }

    .header-content {
        flex-direction: column;
    }

    .header-left {
        width: 100%;
    }

    .status-path-container {
        flex-direction: column;
        align-items: stretch;
    }

    .status-path {
        overflow-x: auto;
        padding-bottom: 5px;
        /* Scrollbar space */
    }

    .metrics-section {
        grid-template-columns: 1fr;
        padding: 0 1rem 1rem;
    }

    .two-column-layout {
        grid-template-columns: 1fr;
        padding: 0 1rem 1rem;
    }

    .opportunity-title {
        font-size: 20px;
    }
}
</style>